import { createCheckboxIndicator } from './checkbox-indicator.js';

const LOADING_FALLBACK_DELAY = 140;

const config = document.querySelector('[data-for-you-controller]');
const bootstrap = document.querySelector('[data-for-you-bootstrap]');
if (config && bootstrap?.favouriteIntent) {
  config.favouriteIntent ||= bootstrap.favouriteIntent;
  bootstrap.favouriteIntent = null;
}
if (config) initialize().catch(() => {
  const feedback = document.createElement('div');
  feedback.className = 'for-you-global-feedback';
  feedback.setAttribute('role', 'status');
  document.body.append(feedback);
  config.handleFavourite = button => {
    button.setAttribute('aria-busy', 'false');
    feedback.textContent = 'Favourites couldn’t load. Refresh the page and try again.';
  };
  if (config.favouriteIntent) {
    config.handleFavourite(config.favouriteIntent);
    config.favouriteIntent = null;
  }
  const loading = document.querySelector('[data-for-you-loading]');
  if (loading) {
    window.clearTimeout(loading.forYouLoadingTimer);
    loading.forYouLoadingTimer = 0;
    loading.hidden = false;
    const shape = loading.querySelector('[data-for-you-loading-shape]');
    shape.hidden = true;
    shape.closest('[data-for-you-loading-shape-host]')?.setAttribute('hidden', '');
    const status = loading.querySelector('[data-for-you-loading-status]');
    status.classList.remove('for-you__announcement');
    status.textContent = 'For You couldn’t load. You can still browse coffee below.';
  }
});
async function initialize() {
  const app = document.querySelector('[data-for-you-app]');
  const [{ createForYouClient, productId, productHandle, allowHistory, nativeHistoryPermission, loadHistoryPrivacy, errorCopy }, { createForYouDialog }, { questions, validAnswers }, { hydrateCards, createCardRequestLimit, personalizedRows }, { createFavoriteQueue }, { initializeRails }, { initializeCollectionSheet } = {}] = await Promise.all([
    import(config.dataset.clientUrl), import(config.dataset.dialogUrl), import(config.dataset.quizUrl), import(config.dataset.renderUrl), import(config.dataset.queueUrl), import(config.dataset.railUrl),
    app ? import(config.dataset.cardModesUrl) : Promise.resolve(),
  ]);
  const fixture = app?.dataset.fixture === 'true';
  const rootPath = config.dataset.rootUrl || '/';
  const fallback = document.querySelector('[data-for-you-fallback]');
  const loading = document.querySelector('[data-for-you-loading]');
  const loginFallback = document.querySelector('[data-for-you-login-fallback]');
  const retry = document.querySelector('[data-for-you-retry]');
  function loadingState(pending, message = '') {
    if (!loading) return;
    window.clearTimeout(loading.forYouLoadingTimer);
    loading.forYouLoadingTimer = 0;
    const shape = loading.querySelector('[data-for-you-loading-shape]');
    const shapeHost = shape.closest('[data-for-you-loading-shape-host]');
    const status = loading.querySelector('[data-for-you-loading-status]');
    status.classList.toggle('for-you__announcement', pending);
    status.textContent = pending ? 'Loading For You…' : message;
    app?.setAttribute('aria-busy', String(pending));
    shape.hidden = true;
    if (shapeHost) shapeHost.hidden = true;
    loading.hidden = !pending && !message;
    if (!pending) return;
    loading.forYouLoadingTimer = window.setTimeout(() => {
      loading.forYouLoadingTimer = 0;
      if (app?.getAttribute('aria-busy') !== 'true') return;
      if (shapeHost) shapeHost.hidden = false;
      shape.hidden = false;
    }, LOADING_FALLBACK_DELAY);
  }
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  if (app) initializeRails(app, reducedMotion);
  const removeDialog = document.querySelector('[data-for-you-remove-dialog]');
  const removalMotion = createForYouDialog({ dialog: removeDialog, reducedMotion });
  let removal = null;
  async function closeRemoval(immediate = false) {
    const trigger = removal?.trigger; removal = null;
    const closed = await removalMotion.close(immediate === true);
    if (closed && trigger?.isConnected && trigger.getClientRects().length) trigger.focus({ preventScroll: true });
  }
  removeDialog?.querySelectorAll('[data-for-you-remove-cancel]').forEach(node => node.addEventListener('click', closeRemoval));
  removeDialog?.addEventListener('cancel', event => { event.preventDefault(); closeRemoval(); });
  removeDialog?.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const controls = [...removeDialog.querySelectorAll('button')].filter(node => !node.disabled);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  removeDialog?.querySelector('[data-for-you-remove-confirm]').addEventListener('click', async () => {
    const pending = removal;
    if (!pending || snapshot?.accountKey !== pending.accountKey) { closeRemoval(); return; }
    await closeRemoval();
    if (snapshot?.accountKey === pending.accountKey) favourite(pending.trigger, true);
  });
  const accountDialog = document.querySelector('[data-for-you-account-dialog]');
  const accountMotion = createForYouDialog({ dialog: accountDialog, reducedMotion });
  let accountInvitation = null;
  async function closeAccountInvitation(immediate = false) {
    const trigger = accountInvitation?.trigger; accountInvitation = null;
    const closed = await accountMotion.close(immediate === true);
    if (closed && trigger?.isConnected && trigger.getClientRects().length) trigger.focus({ preventScroll: true });
  }
  accountDialog?.querySelectorAll('[data-for-you-account-cancel]').forEach(node => node.addEventListener('click', closeAccountInvitation));
  accountDialog?.addEventListener('cancel', event => { event.preventDefault(); closeAccountInvitation(); });
  accountDialog?.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const controls = [...accountDialog.querySelectorAll('button, a[href]')];
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  accountDialog?.querySelectorAll('[data-for-you-account-continue]').forEach(node => node.addEventListener('click', event => {
    if (!accountInvitation || serviceStatus !== 'guest') { event.preventDefault(); closeAccountInvitation(); return; }
    try {
      sessionStorage.setItem('sigma-for-you-pending-favourite', JSON.stringify({ productId: accountInvitation.productId, title: accountInvitation.title, expires: Date.now() + 600000 }));
    } catch {
      event.preventDefault(); closeAccountInvitation(); tell('Log in first, then return to this product to save it.');
    }
  }));
  const availableViews = ['feed', 'favourites', 'taste', 'privacy'];
  const initialView = location.hash.slice(1);
  let snapshot = null, view = availableViews.includes(initialView) ? initialView : 'feed', visibleCount = 6, renderVersion = 0, undo = null, hasViewed = false;
  const FAVORITES_ALL = 'all';
  const FAVORITES_UNKNOWN = 'unknown:';
  const favouriteNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const favouriteBatchSize = () => {
    if (window.innerWidth >= 1400) return 16;
    if (window.innerWidth >= 1024) return 12;
    if (window.innerWidth >= 750) return 8;
    return 6;
  };
  let favouriteFilter = FAVORITES_ALL, favouriteSort = 'newest', favouriteVisibleCount = favouriteBatchSize();
  let favouriteLoadMoreBusy = false;
  let mutationBusy = false, serviceStatus = fixture ? 'ready' : 'unknown';
  let tasteDraft = null, tasteMutationIntent = null;
  let refreshAfterMutation = false, optimisticFavorites = new Set(), earlyFavourite = null, pageLeaving = false;
  let saveQueue;
  let toastModule;
  const toasts = () => toastModule ||= import(config.dataset.toastUrl);
  const saveError = () => { toasts().then(module => module.notifyError('We couldn’t confirm that save. Check the heart and try again.')).catch(() => tell('We couldn’t confirm that save. Check the heart and try again.')); };
  let accountChannel = null;
  if (!fixture && typeof BroadcastChannel === 'function') {
    accountChannel = new BroadcastChannel('sigma-for-you-account-refresh-v1');
    accountChannel.addEventListener('message', event => {
      if (event.data?.type !== 'changed') return;
      if (mutationBusy) { refreshAfterMutation = true; return; }
      if (document.hidden) client.invalidate();
      else load();
    });
  }
  /* ─────────────────────────────────────────────────────────
   * PANEL TRANSITION STORYBOARD
   *
   * Read top-to-bottom. Each value is ms after a pointer switch.
   *
   *    0ms   old panel exits opposite the destination; new panel enters from its chip
   *  260ms   destination rests at x 0 and becomes the only visible panel
   *
   * Keyboard switches and reduced motion settle immediately at 0ms.
   * ───────────────────────────────────────────────────────── */
  const PANEL_TIMING = {
    settle: 260, // paired panel movement completes
  };
  const PANEL_MOTION = {
    offset: 24, // px travelled by each panel
    enteringOpacity: 0.35, // keeps the paired scene visually continuous
    easing: 'cubic-bezier(0.65, 0, 0.35, 1)', // smooth movement without bounce
  };
  const panelStage = app?.querySelector('[data-for-you-panel-stage]');
  let panelMotion = null;
  let panelMotionId = 0;
  function panelFor(name) { return app?.querySelector(`[data-for-you-panel="${name}"]`); }
  function clearPanelMotionStyles(panel) {
    if (!panel) return;
    panel.style.removeProperty('transform');
    panel.style.removeProperty('opacity');
  }
  function settlePanelVisibility() {
    app?.querySelectorAll('[data-for-you-panel]').forEach(panel => {
      const active = panel.dataset.forYouPanel === view;
      panel.hidden = !active;
      panel.inert = !active;
      clearPanelMotionStyles(panel);
    });
    panelStage?.removeAttribute('data-for-you-panel-moving');
  }
  function finishPanelMotion({ preserveActive = false } = {}) {
    const motion = panelMotion;
    panelMotion = null;
    panelMotionId++;
    if (motion) {
      clearTimeout(motion.fallback);
      motion.animations.forEach(({ animation, panel }) => {
        if (preserveActive && panel.dataset.forYouPanel === view) {
          try { animation.commitStyles(); } catch { /* The next motion can start from rest. */ }
        }
        animation.cancel();
      });
    }
    app?.querySelectorAll('[data-for-you-panel]').forEach(panel => {
      const active = panel.dataset.forYouPanel === view;
      panel.hidden = !active;
      panel.inert = !active;
      if (!preserveActive || !active) clearPanelMotionStyles(panel);
    });
    panelStage?.removeAttribute('data-for-you-panel-moving');
  }
  function startPanelMotion(outgoing, incoming, direction) {
    if (!outgoing || !incoming || !panelStage || typeof incoming.animate !== 'function') {
      settlePanelVisibility();
      return;
    }
    const id = ++panelMotionId;
    const exitX = direction * -PANEL_MOTION.offset;
    const enterX = direction * PANEL_MOTION.offset;
    outgoing.hidden = false;
    outgoing.inert = true;
    incoming.hidden = false;
    incoming.inert = false;
    panelStage.setAttribute('data-for-you-panel-moving', '');
    const timing = { duration: PANEL_TIMING.settle, easing: PANEL_MOTION.easing, fill: 'both' };
    const outgoingStart = {
      transform: outgoing.style.transform || 'translate3d(0, 0, 0)',
      opacity: outgoing.style.opacity || '1',
    };
    const animations = [];
    try {
      animations.push({ panel: outgoing, animation: outgoing.animate([outgoingStart, { transform: `translate3d(${exitX}px, 0, 0)`, opacity: 0 }], timing) });
      animations.push({ panel: incoming, animation: incoming.animate([
        { transform: `translate3d(${enterX}px, 0, 0)`, opacity: PANEL_MOTION.enteringOpacity },
        { transform: 'translate3d(0, 0, 0)', opacity: 1 },
      ], timing) });
    } catch {
      animations.forEach(({ animation }) => animation.cancel());
      settlePanelVisibility();
      return;
    }
    const complete = () => {
      if (panelMotion?.id !== id || view !== incoming.dataset.forYouPanel) return;
      finishPanelMotion();
    };
    panelMotion = {
      id,
      outgoing,
      incoming,
      animations,
      fallback: setTimeout(complete, PANEL_TIMING.settle + 50),
    };
    Promise.allSettled(animations.map(({ animation }) => animation.finished)).then(complete);
  }
  reducedMotion.addEventListener('change', () => finishPanelMotion());
  function navigate(next, event) {
    if (!availableViews.includes(next) || next === view) return;
    const tabs = [...app.querySelectorAll('[data-for-you-view]')];
    const outgoing = panelFor(view);
    const direction = tabs.findIndex(tab => tab.dataset.forYouView === next) > tabs.findIndex(tab => tab.dataset.forYouView === view) ? 1 : -1;
    finishPanelMotion({ preserveActive: true });
    view = next;
    render({ outgoingPanel: outgoing });
    app.querySelector(`[data-for-you-view="${view}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const incoming = panelFor(view);
    if (reducedMotion.matches || event?.detail === 0) {
      finishPanelMotion();
      return;
    }
    startPanelMotion(outgoing, incoming, direction);
  }
  const cardCache = new Map();
  const cardPlaceholderTimers = new Map();
  const limitCardRequest = createCardRequestLimit(4);
  let deferredRows = [], focusReturn = null, feedAppendingVersion = null;
  const cardScope = target => target?.dataset.forYouRowKey || target?.id || (target?.hasAttribute('data-for-you-favourites') ? 'favourites' : 'compatibility');
  function preserveCardFocus() {
    if (focusReturn && focusReturn.view !== view) focusReturn = null;
    const active = document.activeElement;
    if (!app.contains(active) || !active.matches('[data-for-you-favourite]:focus-visible')) return;
    const target = active.closest('[data-for-you-track], [data-for-you-favourites]');
    const fallback = app.querySelector(`[data-for-you-view="${view}"]`);
    focusReturn = { id: active.dataset.forYouFavourite, scope: cardScope(target), view, accountKey: snapshot.accountKey, fallback };
    fallback.focus({ preventScroll: true });
  }
  function restoreCardFocus(target) {
    if (!focusReturn || focusReturn.accountKey !== snapshot?.accountKey || focusReturn.scope !== cardScope(target)) return;
    if (document.activeElement !== focusReturn.fallback) { focusReturn = null; return; }
    const controls = [...target.querySelectorAll('[data-for-you-favourite]')];
    const next = controls.find(control => control.dataset.forYouFavourite === focusReturn.id) || controls[0];
    if (next && !next.hidden) { next.focus(); focusReturn = null; }
  }
  function clearDeferredRows() { deferredRows.forEach(cleanup => cleanup()); deferredRows = []; }
  function clearCardPlaceholder(wrapper) {
    const timer = cardPlaceholderTimers.get(wrapper);
    if (timer) window.clearTimeout(timer);
    cardPlaceholderTimers.delete(wrapper);
    wrapper.style.removeProperty('min-height');
  }
  function clearCardPlaceholders(root = app) {
    cardPlaceholderTimers.forEach((timer, wrapper) => {
      if (!root?.contains(wrapper)) return;
      window.clearTimeout(timer);
      cardPlaceholderTimers.delete(wrapper);
    });
  }
  const fixtureCards = new Map([...document.querySelectorAll('[data-for-you-fixture-card]')].map(t => [t.dataset.id, t]));
  const fixtureItems = [...fixtureCards.values()].map(t => ({
    id: t.dataset.id, handle: t.dataset.handle, title: t.dataset.title, available: t.dataset.available === 'true', variants: [],
    fixtureCollection: t.dataset.fixtureCollection || '', productType: t.dataset.productType || '', origin: t.dataset.origin || '', process: t.dataset.process || '',
    reasons: ['Preview example: discovery from the current catalogue.'],
  }));
  const fixtureCoffeeTypes = new Set(['coffee', 'coffee beans', 'roasted coffee', 'whole bean coffee']);
  const fixtureEquipmentRows = new Set(['fixture-filter-products', 'fixture-brewing-equipment']);
  function fixtureRows() {
    const available = fixtureItems.filter(item => item.available);
    const claimed = new Set();
    const makeRow = (id, title, description, candidates, reasons, minimum = 3) => {
      const items = candidates.filter(item => !claimed.has(item.id)).slice(0, 6);
      if (items.length < minimum) return null;
      items.forEach(item => claimed.add(item.id));
      return { id, title, description, items: items.map(item => ({ ...item, reasons: reasons(item) })) };
    };
    const coffee = available.filter(item => fixtureCoffeeTypes.has(item.productType.trim().toLowerCase()));
    const coffeeWithDetails = coffee.filter(item => item.origin || item.process);
    const originCandidates = coffeeWithDetails.filter(item => item.origin);
    const originSample = [];
    for (const item of originCandidates) {
      if (originSample.length === 6) break;
      const remainingDetails = coffeeWithDetails.filter(candidate => candidate.id !== item.id && !originSample.some(selected => selected.id === candidate.id));
      if (remainingDetails.length < 3) break;
      originSample.push(item);
    }
    const rows = [
      makeRow('fixture-origins', 'Origins to explore', 'Preview example: coffees grouped from recorded catalogue details.', originSample, item => [`Preview example: Recorded origin: ${item.origin}.`]),
      makeRow('fixture-coffee-details', 'Coffee characteristics to explore', 'Preview example: more coffees with recorded origin or processing details.', coffeeWithDetails, item => {
        const details = [];
        if (item.process) details.push(`Preview example: Recorded process: ${item.process}.`);
        if (item.origin) details.push(`Recorded origin: ${item.origin}.`);
        return details;
      }),
      makeRow('fixture-filter-products', 'Filter brewing essentials', 'Preview example: available products from the current filter brewing collection.', available.filter(item => item.fixtureCollection === 'filters'), () => ['Preview example: Listed in Sigma’s filter brewing collection.'], 1),
      makeRow('fixture-brewing-equipment', 'Brewing equipment to explore', 'Preview example: available products from the current brewing equipment collection.', available.filter(item => item.fixtureCollection === 'brewers'), () => ['Preview example: Listed in Sigma’s brewing equipment collection.'], 1),
    ];
    return rows.filter(Boolean);
  }
  const fixtureBase = () => ({ version: 1, available: true, accountKey: 'fixture-only', revision: 0, mutationToken: 'fixture-only', profile: { quiz: null, favorites: [], lessLike: [], setup: [], historyEnabled: false }, feed: { items: fixtureItems.filter(p => p.available).slice(0, 24), rows: fixtureRows(), nextCursor: null }, favorites: [], recentlyViewed: [], compatibility: [], capabilities: { browsingHistory: true, verifiedCompatibility: false }, history: { count: 0, retentionDays: 30 } });
  function fixturePreviewRows(feed) {
    return (Array.isArray(feed?.rows) ? feed.rows : []).filter(row => row && typeof row.id === 'string' && typeof row.title === 'string' && Array.isArray(row.items)).map(row => ({
      ...row,
      items: row.items.filter((item, index, items) => item?.available === true && /^\d+$/.test(String(item.id)) && /^[a-z0-9][a-z0-9-]*$/.test(item.handle || '') && items.findIndex(other => other?.id === item.id) === index).slice(0, 6),
    })).filter(row => row.items.length >= (fixtureEquipmentRows.has(row.id) ? 1 : 3)).slice(0, 4);
  }
  const el = (tag, text, className) => { const node = document.createElement(tag); if (text) node.textContent = text; if (className) node.className = className; return node; };
  const icons = new Map([...document.querySelectorAll('[data-for-you-icon]')].map(node => [node.dataset.forYouIcon, node]));
  function setButtonText(node, text, icon) {
    let label = node.querySelector('[data-for-you-label]');
    if (!label) {
      node.replaceChildren();
      if (icons.has(icon)) node.append(icons.get(icon).content.cloneNode(true));
      label = el('span'); label.dataset.forYouLabel = ''; node.append(label);
    }
    label.textContent = text;
  }
  const button = (text, fn, primary = false) => {
    const b = el('button', '', `button button--compact${primary ? ' button--primary' : ''}`); b.type = 'button';
    const icon = { 'Show me less like this': 'thumbs-down', Undo: 'undo', 'Save favourite': 'heart', 'Remove favourite': 'heart', 'Save to my account': 'preferences' }[text];
    setButtonText(b, text, icon);
    if (icon) b.dataset.iconText = '';
    if (icon === 'thumbs-down') b.className = 'collection-preference__help for-you__less';
    if (fn) b.addEventListener('click', fn); return b;
  };
  const status = app?.querySelector('[data-for-you-status]') || (() => { const n = el('div', '', 'for-you-global-feedback'); n.setAttribute('role', 'status'); document.body.append(n); return n; })();
  const announcement = el('div', '', 'for-you__announcement'); announcement.setAttribute('role', 'status'); document.body.append(announcement);
  function tell(message) { status.textContent = message; }
  function tasteTell(message) {
    const tasteStatus = app?.querySelector('[data-for-you-taste-status]');
    if (tasteStatus) tasteStatus.textContent = message;
    else tell(message);
  }
  const emptyQuiz = () => Object.fromEntries(questions.map(question => [question.key, []]));
  const copyQuiz = quiz => Object.fromEntries(questions.map(question => [question.key, Array.isArray(quiz?.[question.key]) ? [...quiz[question.key]] : []]));
  function setTasteDraftFromSnapshot() {
    if (!snapshot) { tasteDraft = null; return; }
    tasteDraft = { accountKey: snapshot.accountKey, answers: copyQuiz(snapshot.profile.quiz), dirty: false };
  }
  function clearTasteDraft(clearQuestions = false) {
    tasteDraft = null;
    if (clearQuestions) app?.querySelector('[data-for-you-questions]')?.replaceChildren();
  }
  function setTasteBusy(pending, intent = tasteMutationIntent) {
    const form = app?.querySelector('[data-for-you-taste]');
    if (!form) return;
    form.setAttribute('aria-busy', String(pending));
    form.querySelectorAll('input, button').forEach(control => { control.disabled = pending; });
    const importer = app.querySelector('[data-for-you-import]');
    if (importer) importer.disabled = pending;
    const labels = [
      [app.querySelector('[data-for-you-save-taste]'), intent === 'save' ? 'Saving…' : 'Save preferences', 'Save preferences'],
      [app.querySelector('[data-for-you-clear-taste]'), intent === 'clear' ? 'Clearing…' : 'Clear preferences', 'Clear preferences'],
      [importer, intent === 'import' ? 'Saving…' : 'Use this browser’s saved taste profile', 'Use this browser’s saved taste profile'],
    ];
    labels.forEach(([control, busyLabel, idleLabel]) => { if (control) control.textContent = pending ? busyLabel : idleLabel; });
  }
  function resetFavouriteView() {
    favouriteFilter = FAVORITES_ALL;
    favouriteSort = 'newest';
    favouriteVisibleCount = favouriteBatchSize();
    favouriteLoadMoreBusy = false;
    app?.querySelectorAll('[data-for-you-product-type-dialog], [data-for-you-sort-dialog]').forEach(dialog => {
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
      dialog.removeAttribute('aria-busy');
    });
    app?.querySelectorAll('[data-for-you-product-type-open], [data-for-you-sort-open]').forEach(trigger => {
      setFavouriteLoading(trigger, false);
    });
    const control = app?.querySelector('[data-collection-load-more-button]');
    if (control instanceof HTMLButtonElement) {
      control.disabled = false;
      setFavouriteLoading(control, false);
    }
  }
  function setSnapshot(value) {
    const changedAccount = snapshot && value && snapshot.accountKey !== value.accountKey;
    const changedTasteAccount = tasteDraft && value && tasteDraft.accountKey !== value.accountKey;
    if (changedTasteAccount) clearTasteDraft(true);
    if (changedAccount) { saveQueue?.invalidate(); toastModule?.then(module => module.clearNotifications()); }
    snapshot = value;
    serviceStatus = value ? 'ready' : 'unknown';
    if (changedAccount || !value) {
      resetFavouriteView();
      undo = null; visibleCount = 6; feedAppendingVersion = null; renderVersion++; status.replaceChildren(); announcement.textContent = ''; finishPanelMotion(); closeRemoval(true); closeAccountInvitation(true);
      document.querySelectorAll('[data-for-you-pending-confirmation]').forEach(node => node.remove());
      if (app) {
        clearDeferredRows(); clearCardPlaceholders(); focusReturn = null;
        app.hidden = true;
        app.querySelectorAll('[data-for-you-feed], [data-for-you-favourites], [data-for-you-recent], [data-for-you-rows]').forEach(node => node.replaceChildren());
      }
    }
    syncButtons();
    window.dispatchEvent(new Event('sigma:for-you-readiness'));
    if (value && app) { app.hidden = false; app.closest('section').setAttribute('aria-labelledby', 'ForYouAppTitle'); if (loginFallback) loginFallback.hidden = true; if (fallback) fallback.hidden = true; loadingState(false); if (retry) retry.hidden = true; if (!value.stateOnly) render(); }
    if (app) app.querySelector('[data-for-you-undo]').hidden = !undo;
  }
  const client = createForYouClient({ onChange: setSnapshot, stateOnly: !app });
  let intentStorage; try { intentStorage = sessionStorage; } catch { /* Optional navigation recovery. */ }
  saveQueue = createFavoriteQueue({ getSnapshot: () => snapshot, canStart: () => !mutationBusy,
    mutate: async id => { if (!await change('favorite', { productId: id, enabled: true }, '')) throw new Error('unconfirmed'); },
    onPending: ids => { const finished = optimisticFavorites.size && !ids.size; optimisticFavorites = ids; syncButtons(); if (finished && app && !pageLeaving && snapshot) { render(); client.refreshFeed().catch(() => {}); } if (!ids.size) resumeFavouriteIntent(); },
    onConfirmed: () => { toasts().then(module => { if (!pageLeaving) module.notifySaved(); }).catch(() => { announcement.textContent = 'Saved to your favourites.'; }); },
    onError: saveError, storage: intentStorage,
  });
  const isGuest = (error) => error?.status === 401 || ['login_required', 'unauthorized'].includes(error?.code);
  function settleGuest({ preserveServerFallback = false } = {}) {
    resetFavouriteView();
    serviceStatus = 'guest';
    clearTasteDraft(true);
    window.dispatchEvent(new Event('sigma:for-you-readiness'));
    finishPanelMotion();
    if (app) app.hidden = true;
    if (fallback) fallback.hidden = false;
    loadingState(false);
    if (retry) retry.hidden = preserveServerFallback;
    syncButtons(true);
    if (earlyFavourite) {
      const trigger = earlyFavourite;
      earlyFavourite = null;
      favourite(trigger, false, false);
    }
    if (loginFallback && !preserveServerFallback) {
      loginFallback.hidden = false;
      if (fallback) fallback.hidden = true;
    }
  }
  function syncButtons(guest = serviceStatus === 'guest') {
    document.querySelectorAll('[data-for-you-favourite]').forEach(b => {
      if (!b.classList.contains('for-you-favourite--purchase')) b.classList.remove('button', 'button--compact');
      const id = b.dataset.forYouFavourite;
      const queued = optimisticFavorites.has(id);
      const saved = queued || snapshot?.profile.favorites.includes(id) || false;
      const unknown = !snapshot && !guest;
      b.hidden = false;
      b.disabled = false;
      b.toggleAttribute('data-for-you-unknown', unknown);
      b.setAttribute('aria-disabled', String(queued));
      b.setAttribute('aria-busy', String(queued || earlyFavourite === b || config.favouriteIntent === b));
      if (unknown) b.removeAttribute('aria-pressed'); else b.setAttribute('aria-pressed', String(saved));
      if (!b.querySelector('.for-you-favourite__outline')) {
        b.replaceChildren(document.querySelector('[data-for-you-heart]').content.cloneNode(true));
      }
      b.title = unknown ? 'Checking favourites' : guest ? 'Log in to save' : saved ? 'Remove favourite' : 'Save favourite';
      const title = b.dataset.productTitle || 'this product';
      b.setAttribute('aria-label', unknown ? `Checking whether ${title} is saved` : queued ? `Saving ${title} to favourites` : guest ? `Log in to save ${title}` : saved ? `Remove ${title} from favourites` : `Save ${title} to favourites`);
    });
  }
  async function load() {
    if (fixture) { setSnapshot(fixtureBase()); return; }
    if (mutationBusy) return;
    loadingState(true);
    try {
      await client.load();
      const trigger = earlyFavourite;
      earlyFavourite = null;
      if (!saveQueue.pending) saveQueue.restore();
      // An explicit favourite action takes priority over optional view recording.
      if (trigger) favourite(trigger, false, false);
      else maybeRecordView();
    }
    catch (error) {
      if (error?.code === 'account_changed' || error?.name === 'AbortError') return;
      if (isGuest(error)) {
        settleGuest();
        return;
      }
      resetFavouriteView();
      serviceStatus = 'unavailable';
      window.dispatchEvent(new Event('sigma:for-you-readiness'));
      finishPanelMotion();
      if (app) app.hidden = true;
      if (fallback) fallback.hidden = false;
      loadingState(false, 'Your personalised page isn’t available right now. You can still browse coffee below.');
      if (retry) retry.hidden = false;
      if (earlyFavourite) { earlyFavourite = null; tell(errorCopy(error)); syncButtons(); }
    }
  }
  async function change(action, values, message, options = {}) {
    const report = options.taste === true ? tasteTell : tell;
    if (mutationBusy) { report('Please wait for your current change to finish.'); return false; }
    if (!snapshot) { report('Log in to save this to your account.'); return false; }
    mutationBusy = true; syncButtons();
    tasteMutationIntent = options.tasteIntent || null;
    setTasteBusy(true, tasteMutationIntent);
    app?.querySelectorAll('form button, [data-for-you-history]').forEach(b => b.disabled = true);
    try {
      if (fixture) {
        const next = structuredClone(snapshot);
        if (action === 'favorite') {
          next.profile.favorites = next.profile.favorites.filter(id => id !== values.productId);
          if (values.enabled) next.profile.favorites.push(values.productId);
          next.favorites = fixtureItems.filter(p => next.profile.favorites.includes(p.id));
        }
        if (action === 'less_like') {
          next.profile.lessLike = next.profile.lessLike.filter(id => id !== values.productId);
          if (values.enabled) next.profile.lessLike.push(values.productId);
        }
        if (action === 'save_quiz') next.profile.quiz = values.quiz;
        if (action === 'save_setup') next.profile.setup = values.setup;
        if (action === 'history_consent') { next.profile.historyEnabled = values.enabled; next.recentlyViewed = values.enabled ? fixtureItems.slice(-2) : []; next.history.count = next.recentlyViewed.length; }
        if (action === 'clear_history') { next.history.count = 0; next.recentlyViewed = []; }
        if (action === 'reset') { next.profile = { quiz: null, favorites: [], lessLike: [], setup: [], historyEnabled: false }; next.favorites = []; next.recentlyViewed = []; next.history.count = 0; }
        next.feed.items = fixtureItems.filter(p => p.available && !next.profile.lessLike.includes(p.id)).slice(0, 24);
        next.feed.rows = fixtureRows().map(row => ({ ...row, items: row.items.filter(item => !next.profile.lessLike.includes(item.id)) }));
        next.revision++;
        setSnapshot(next);
      } else {
        await client.mutate(action, values);
        accountChannel?.postMessage({ type: 'changed' });
      }
      if (action === 'save_quiz' || action === 'reset') {
        setTasteDraftFromSnapshot();
        questions.forEach(question => clearTasteQuestionError(question.key));
        tasteTell('');
        if (view === 'taste') renderTaste({ syncValues: true });
      }
      if (message) report(`${fixture ? 'Preview only. ' : ''}${message}`);
      return true;
    } catch (error) {
      report(errorCopy(error));
      if (!snapshot) {
        serviceStatus = isGuest(error) ? 'guest' : 'unavailable';
        if (isGuest(error)) clearTasteDraft(true);
        window.dispatchEvent(new Event('sigma:for-you-readiness'));
        if (fallback) fallback.hidden = false; if (retry) retry.hidden = false;
        loadingState(false, errorCopy(error));
        if (isGuest(error) && loginFallback) { loginFallback.hidden = false; if (fallback) fallback.hidden = true; }
      }
      return false;
    } finally {
      mutationBusy = false; syncButtons();
      app?.querySelectorAll('form button').forEach(b => b.disabled = false);
      setTasteBusy(false, tasteMutationIntent);
      tasteMutationIntent = null;
      syncHistoryControl();
      if (refreshAfterMutation && !saveQueue.pending) { refreshAfterMutation = false; if (document.hidden) client.invalidate(); else load(); }
      queueMicrotask(() => { saveQueue.drain(); resumeFavouriteIntent(); });
    }
  }
  function resumeFavouriteIntent() {
    if (!earlyFavourite || !snapshot || mutationBusy || saveQueue.pending || pageLeaving) return;
    const trigger = earlyFavourite;
    earlyFavourite = null;
    favourite(trigger, false, false);
  }
  async function favourite(b, confirmed = false, gesture = true) {
    const id = productId(b.dataset.forYouFavourite);
    if (optimisticFavorites.has(id)) return;
    if (!id) return;
    b.setAttribute('aria-busy', 'false');
    if (gesture && !confirmed && !snapshot?.profile.favorites.includes(id)) window.SigmaThemeAudio?.playFavouriteTap();
    if (!snapshot) {
      if (serviceStatus !== 'guest') {
        earlyFavourite = b;
        announcement.textContent = 'Checking your favourites…';
        syncButtons();
        if (serviceStatus === 'unavailable') load();
        return;
      }
      if (!accountDialog?.showModal) { tell('Log in from My account, then return to this product to save it.'); return; }
      accountInvitation = { trigger: b, productId: id, title: b.dataset.productTitle || 'this product' };
      accountDialog.querySelector('[data-for-you-account-copy]').textContent = `Keep ${accountInvitation.title} in your favourites, ready for your next visit.`;
      accountMotion.open();
      accountDialog.querySelector('[data-for-you-account-continue]').focus({ preventScroll: true });
      return;
    }
    const wasSaved = snapshot.profile.favorites.includes(id), accountKey = snapshot.accountKey;
    if (!wasSaved && snapshot.profile.favorites.length + optimisticFavorites.size >= 100) { tell('You can save up to 100 favourites. Remove one before adding another.'); return; }
    if (!wasSaved) {
      const started = saveQueue.add(id);
      if (started && !reducedMotion.matches) document.querySelectorAll(`[data-for-you-favourite="${id}"] .for-you-favourite__filled`).forEach(icon => icon.animate([{ transform: 'scale(0.85)' }, { transform: 'scale(1.13)', offset: 0.45 }, { transform: 'scale(1)' }], { duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }));
      return;
    }
    if (mutationBusy || saveQueue.pending) {
      earlyFavourite = b;
      announcement.textContent = 'Finishing your current change before opening this favourite…';
      syncButtons();
      return;
    }
    if (wasSaved && !confirmed) {
      if (!removeDialog?.showModal) { tell('Your favourite has been kept. This browser couldn’t open the confirmation.'); return; }
      removal = { trigger: b, accountKey };
      removeDialog.querySelector('[data-for-you-remove-copy]').textContent = `Remove ${b.dataset.productTitle || 'this product'} from your favourites?`;
      removalMotion.open();
      removeDialog.querySelector('button[autofocus]').focus({ preventScroll: true });
      return;
    }
    b.setAttribute('aria-pressed', String(!wasSaved));
    b.setAttribute('aria-busy', 'true');
    const ok = await change('favorite', { productId: id, enabled: !wasSaved }, wasSaved ? '' : 'Saved to your favourites.');
    b.removeAttribute('aria-busy');
    if (ok && snapshot?.accountKey === accountKey) {
      if (!wasSaved && !reducedMotion.matches) {
        requestAnimationFrame(() => document.querySelectorAll(`[data-for-you-favourite="${id}"]`).forEach(control => {
          const icon = control.querySelector('.for-you-favourite__filled');
          icon?.animate([{ transform: 'scale(0.85)' }, { transform: 'scale(1.13)', offset: 0.45 }, { transform: 'scale(1)' }], { duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
        }));
      }
      undo = null;
      if (app) app.querySelector('[data-for-you-undo]').hidden = true;
      if (wasSaved) { tell(''); announcement.textContent = 'Favourite removed.'; if (app) render(); }
      if (app && !b.isConnected) requestAnimationFrame(() => {
        const replacement = [...document.querySelectorAll(`[data-for-you-favourite="${id}"]`)].find(node => node.getClientRects().length);
        const tab = app.querySelector(`[data-for-you-view="${view}"]`);
        (replacement || tab)?.focus({ preventScroll: true });
      });
    }
    syncButtons();
  }
  config.handleFavourite = favourite;
  if (config.favouriteIntent) {
    const trigger = config.favouriteIntent;
    config.favouriteIntent = null;
    favourite(trigger);
  }
  const observer = new MutationObserver(records => { if (records.some(r => [...r.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-for-you-favourite]') || n.querySelector?.('[data-for-you-favourite]'))))) syncButtons(); });
  observer.observe(document.body, { childList: true, subtree: true });
  function safeProductUrl(handle) { return `${rootPath}products/${encodeURIComponent(handle)}`; }
  async function cardFor(product) {
    const id = productId(product.id), handle = productHandle(product.handle);
    if (!id || !handle) return null;
    if (fixtureCards.has(id)) return fixtureCards.get(id).content.cloneNode(true);
    if (!cardCache.has(id)) cardCache.set(id, limitCardRequest(async () => {
      const response = await fetch(`${safeProductUrl(handle)}?view=for-you-card`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('card_unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('[data-for-you-card-id]');
      if (source?.dataset.forYouCardId !== id) throw new Error('card_mismatch');
      return source;
    }).catch(error => { cardCache.delete(id); throw error; }));
    return (await cardCache.get(id)).cloneNode(true);
  }
  function cardPlaceholders(list, target, append = false) {
    const previousHeights = append
      ? []
      : [...target.children].map(child => child.getBoundingClientRect().height);
    if (!append) {
      clearCardPlaceholders(target);
      target.replaceChildren();
    }
    return list.map((item, index) => {
      const node = el('div', '', 'for-you__result for-you__card-placeholder');
      const previousHeight = previousHeights[index];
      if (previousHeight > 0) node.style.minHeight = `${previousHeight}px`;
      target.append(node);
      const timer = window.setTimeout(() => {
        cardPlaceholderTimers.delete(node);
        if (!node.isConnected || node.childNodes.length) return;
        const skeleton = el('div', '', 'collection-loading-skeleton');
        skeleton.append(app.querySelector('[data-for-you-card-placeholder]').content.cloneNode(true));
        node.append(skeleton);
      }, LOADING_FALLBACK_DELAY);
      cardPlaceholderTimers.set(node, timer);
      return node;
    });
  }
  function deferProducts(list, target, version, recent = false) {
    if (!list.length || typeof IntersectionObserver !== 'function' || focusReturn?.scope === cardScope(target)) { void renderProducts(list, target, version, false, recent); return; }
    cardPlaceholders(list, target); target.setAttribute('aria-busy', 'true');
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      if (version === renderVersion && snapshot) void renderProducts(list, target, version, false, recent);
    }, { rootMargin: '300px 0px' });
    observer.observe(target); deferredRows.push(() => observer.disconnect());
  }
  function renderPersonalizedRows(version) {
    const container = app.querySelector('[data-for-you-rows]'); container.replaceChildren();
    (fixture ? fixturePreviewRows(snapshot.feed) : personalizedRows(snapshot.feed)).forEach((row, index) => {
      const section = app.querySelector('[data-for-you-row-template]').content.firstElementChild.cloneNode(true);
      const heading = section.querySelector('[data-for-you-row-title]'), copy = section.querySelector('[data-for-you-row-copy]');
      heading.id = `ForYouAffinityHeading-${index}`; heading.textContent = row.title;
      section.setAttribute('aria-labelledby', heading.id); copy.textContent = row.description; copy.hidden = !row.description;
      const track = section.querySelector('[data-for-you-track]'); track.id = `ForYouAffinityTrack-${index}`; track.dataset.forYouRowKey = row.id; track.setAttribute('aria-label', row.title);
      section.querySelectorAll('[aria-controls]').forEach(button => button.setAttribute('aria-controls', track.id));
      section.querySelector('[data-for-you-previous]').setAttribute('aria-label', `Show previous products: ${row.title}`);
      section.querySelector('[data-for-you-next]').setAttribute('aria-label', `Show next products: ${row.title}`);
      container.append(section); deferredRows.push(initializeRails(section, reducedMotion));
      deferProducts(row.items, track, version);
    });
  }
  async function renderProducts(list, target, version, saved = false, recent = false, append = false) {
    if (!append) target.replaceChildren();
    target.setAttribute('aria-busy', String(list.length > 0));
    if (!list.length) { target.append(el('p', saved ? 'No favourites yet. Save something you’d like to revisit.' : recent ? (snapshot.capabilities?.browsingHistory !== true ? 'Browsing history isn’t available yet. New views aren’t saved.' : snapshot.profile.historyEnabled ? 'New products you view with browsing permission will appear here.' : 'No saved views yet. You can turn on browsing history in Privacy.') : 'No matches available right now. Update your taste or explore the shop.', 'for-you__copy')); return; }
    const placeholders = cardPlaceholders(list, target, append);
    await hydrateCards(list, async (item, index) => {
      if (version !== renderVersion || !snapshot) return;
      const wrapper = placeholders[index];
      const feedback = el('div', '', 'for-you__recommendation-feedback');
      try {
        const card = await cardFor(item);
        if (version !== renderVersion || !snapshot) return;
        clearCardPlaceholder(wrapper);
        wrapper.replaceChildren();
        if (card && !saved && card.dataset?.available === 'false') { wrapper.append(el('p', 'This product has just sold out. Check back for more matches.', 'for-you__copy')); return; }
        if (card) wrapper.append(card);
        else {
          wrapper.append(el('h3', item.title || 'Unavailable product'));
          wrapper.append(el('p', 'This product is no longer available.', 'for-you__copy'));
          if (saved && productId(item.id)) { const b = button('Remove favourite'); b.dataset.forYouFavourite = String(item.id); wrapper.append(b); }
        }
        if (!saved && !recent && item.reasons?.length) { const reasons = el('ul', '', 'for-you__reasons'); item.reasons.slice(0, 3).forEach(reason => reasons.append(el('li', String(reason)))); feedback.append(reasons); }
      } catch {
        if (version !== renderVersion || !snapshot) return;
        clearCardPlaceholder(wrapper);
        wrapper.replaceChildren();
        wrapper.append(el('p', 'This product couldn’t load. Try again or open its product page.', 'for-you__copy'));
        const handle = productHandle(item.handle);
        if (handle) { const a = el('a', 'View product', 'button button--compact'); a.href = safeProductUrl(handle); wrapper.append(a); }
        if (saved && productId(item.id)) { const b = button('Remove favourite'); b.dataset.forYouFavourite = String(item.id); wrapper.append(b); }
      }
      if (!saved && !recent && productId(item.id)) feedback.append(button('Show me less like this', async () => {
        const key = snapshot?.accountKey;
        if (await change('less_like', { productId: String(item.id), enabled: true }, 'You’ll see fewer products like this. You can undo this.') && snapshot?.accountKey === key) {
          undo = { action: 'less_like', id: String(item.id), enabled: false, accountKey: key };
          const control = app.querySelector('[data-for-you-undo]'); control.hidden = false; control.focus();
        }
      }));
      if (feedback.childNodes.length) wrapper.append(feedback);
      syncButtons();
      wrapper.dispatchEvent(new CustomEvent('sigma:collection-view:updated', { bubbles: true, detail: { view: wrapper } }));
    }, () => version === renderVersion && Boolean(snapshot));
    if (version === renderVersion && snapshot) { target.setAttribute('aria-busy', 'false'); restoreCardFocus(target); if (!append && target.hasAttribute('data-for-you-feed') && target.scrollLeft > 0) void appendFeed(); }
  }
  async function appendFeed() {
    const track = app?.querySelector('[data-for-you-feed]');
    if (!snapshot || view !== 'feed' || !track || feedAppendingVersion !== null || track.getAttribute('aria-busy') === 'true') return;
    if (track.scrollWidth - track.clientWidth - track.scrollLeft > track.clientWidth) return;
    const items = snapshot.feed.items.filter(item => item.available === true).slice(0, 24);
    const next = items.slice(visibleCount, visibleCount + 6);
    if (!next.length) return;
    const version = renderVersion; feedAppendingVersion = version; visibleCount += next.length;
    try {
      await renderProducts(next, track, version, false, false, true);
      if (version === renderVersion && snapshot) app.querySelector('[data-for-you-feed-count]').textContent = `${Math.min(visibleCount, items.length)} of ${items.length} recommendations loaded.`;
    } finally { if (feedAppendingVersion === version) feedAppendingVersion = null; }
  }
  const favouriteProductType = item => typeof item?.productType === 'string' && item.productType !== '' ? item.productType : null;
  const favouriteTypeKey = type => type === null ? FAVORITES_UNKNOWN : `type:${type}`;
  function syncFavouriteFilters(items) {
    const filter = app.querySelector('[data-for-you-favourites-filter]');
    const productTypes = [...new Set(items.map(favouriteProductType).filter(type => type !== null))].sort((a, b) => favouriteNameCollator.compare(a, b));
    const hasUnknown = items.some(item => favouriteProductType(item) === null);
    const choices = [[FAVORITES_ALL, 'All product types'], ...productTypes.map(type => [favouriteTypeKey(type), type])];
    if (hasUnknown) choices.push([FAVORITES_UNKNOWN, 'Unknown']);
    const valid = new Set(choices.map(([value]) => value));
    if (!valid.has(favouriteFilter)) { favouriteFilter = FAVORITES_ALL; favouriteVisibleCount = favouriteBatchSize(); }
    filter.replaceChildren(...choices.map(([value, label]) => {
      const option = el('option', label); option.value = value; return option;
    }));
    filter.value = favouriteFilter;
    const sort = app.querySelector('[data-for-you-favourites-sort]');
    if (!['newest', 'oldest', 'name'].includes(favouriteSort)) favouriteSort = 'newest';
    sort.value = favouriteSort;
    syncFavouriteSelectDialogs();
  }
  function favouriteSelectOption(label, description, kind, value, selected) {
    const option = el('button', '', 'collection-sort-option');
    option.type = 'button';
    option.dataset.forYouSelectKind = kind;
    option.dataset.forYouSelectValue = value;
    if (selected) option.setAttribute('aria-current', 'true');
    const copy = el('span', '', 'collection-sort-option__copy');
    copy.append(el('strong', label), el('small', description));
    option.append(copy, el('span', '', 'collection-sort-option__selector'));
    option.lastElementChild.setAttribute('aria-hidden', 'true');
    return option;
  }
  function syncFavouriteSelectDialogs() {
    const controls = app?.querySelector('[data-for-you-favourites-dialog-controls]');
    if (!controls || controls.hidden) return;
    const filter = app.querySelector('[data-for-you-favourites-filter]');
    const sort = app.querySelector('[data-for-you-favourites-sort]');
    const filterLabel = filter.selectedOptions[0]?.textContent || 'All product types';
    const sortLabel = sort.selectedOptions[0]?.textContent || 'Newest saved';
    const filterTrigger = app.querySelector('[data-for-you-product-type-open]');
    const sortTrigger = app.querySelector('[data-for-you-sort-open]');
    filterTrigger.querySelector('[data-for-you-product-type-label]').textContent = filterLabel;
    filterTrigger.setAttribute('aria-label', `Product type, currently ${filterLabel}`);
    sortTrigger.querySelector('[data-for-you-sort-label]').textContent = sortLabel;
    sortTrigger.setAttribute('aria-label', `Sort by, currently ${sortLabel}`);
    const filterOptions = [...filter.options].map(option => favouriteSelectOption(
      option.textContent,
      option.value === FAVORITES_ALL ? 'Show every saved product' : option.value === FAVORITES_UNKNOWN ? 'Saved products without a product type' : `Show saved ${option.textContent} products`,
      'filter',
      option.value,
      option.value === favouriteFilter,
    ));
    const sortDescriptions = {
      newest: 'Most recently saved first',
      oldest: 'Earliest saved first',
      name: 'Product names in alphabetical order',
    };
    const sortOptions = [...sort.options].map(option => favouriteSelectOption(
      option.textContent,
      sortDescriptions[option.value] || '',
      'sort',
      option.value,
      option.value === favouriteSort,
    ));
    app.querySelector('[data-for-you-product-type-options]').replaceChildren(...filterOptions);
    app.querySelector('[data-for-you-sort-options]').replaceChildren(...sortOptions);
  }
  function initializeFavouriteSelectDialog(kind) {
    if (typeof initializeCollectionSheet !== 'function') return null;
    const prefix = kind === 'filter' ? 'product-type' : 'sort';
    const trigger = app.querySelector(`[data-for-you-${prefix}-open]`);
    const dialog = app.querySelector(`[data-for-you-${prefix}-dialog]`);
    const options = app.querySelector(`[data-for-you-${prefix}-options]`);
    const sheet = initializeCollectionSheet({
      trigger,
      dialog,
      panel: app.querySelector(`[data-for-you-${prefix}-panel]`),
      backdrop: app.querySelector(`[data-for-you-${prefix}-dismiss]`),
      closeButton: app.querySelector(`[data-for-you-${prefix}-close]`),
    });
    if (!sheet) return null;
    options.addEventListener('click', async event => {
      const option = event.target.closest('[data-for-you-select-value]');
      if (!(option instanceof HTMLButtonElement) || option.dataset.forYouSelectKind !== kind) return;
      const value = option.dataset.forYouSelectValue;
      const current = kind === 'filter' ? favouriteFilter : favouriteSort;
      if (value === current) { sheet.close(); return; }
      const accountKey = snapshot?.accountKey;
      if (kind === 'filter') favouriteFilter = value;
      else favouriteSort = value;
      favouriteVisibleCount = favouriteBatchSize();
      app.querySelector(kind === 'filter' ? '[data-for-you-favourites-filter]' : '[data-for-you-favourites-sort]').value = value;
      dialog.setAttribute('aria-busy', 'true');
      options.querySelectorAll('[data-for-you-select-value]').forEach(control => control.setAttribute('aria-disabled', 'true'));
      sheet.close();
      setFavouriteLoading(trigger, true);
      const completion = render();
      try {
        await completion;
      } finally {
        if (snapshot?.accountKey === accountKey) {
          dialog.removeAttribute('aria-busy');
          setFavouriteLoading(trigger, false);
        }
      }
    });
    dialog.addEventListener('keydown', event => {
      const currentOptions = [...options.querySelectorAll('[data-for-you-select-value]')];
      const activeIndex = currentOptions.indexOf(document.activeElement);
      if (activeIndex < 0) return;
      let nextIndex = null;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (activeIndex + 1) % currentOptions.length;
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (activeIndex - 1 + currentOptions.length) % currentOptions.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = currentOptions.length - 1;
      if (nextIndex === null || nextIndex === activeIndex) return;
      event.preventDefault(); currentOptions[nextIndex].focus();
    });
    return sheet;
  }
  function favouriteViewItems(items) {
    const indexed = items.map((item, index) => ({ item, index }));
    indexed.sort((a, b) => {
      if (favouriteSort === 'newest') return b.index - a.index;
      if (favouriteSort === 'oldest') return a.index - b.index;
      const nameA = String(a.item?.title || 'Unavailable product');
      const nameB = String(b.item?.title || 'Unavailable product');
      return favouriteNameCollator.compare(nameA, nameB) || a.index - b.index;
    });
    return favouriteFilter === FAVORITES_ALL
      ? indexed
      : indexed.filter(({ item }) => favouriteTypeKey(favouriteProductType(item)) === favouriteFilter);
  }
  function setFavouriteLoading(button, loading) {
    button.dispatchEvent(new CustomEvent('sigma:button-loading', { bubbles: true, detail: { loading } }));
  }
  function syncFavouriteLoadMore(filteredCount, visibleCount, message = '') {
    const control = app.querySelector('[data-collection-load-more]');
    const button = control.querySelector('[data-collection-load-more-button]');
    const label = control.querySelector('[data-collection-load-more-label]');
    const status = control.querySelector('[data-collection-load-more-status]');
    const remainingCount = Math.max(0, filteredCount - visibleCount);
    control.hidden = filteredCount === 0;
    button.hidden = remainingCount === 0;
    label.textContent = `Load ${Math.min(favouriteBatchSize(), remainingCount)} more`;
    status.textContent = message || (remainingCount === 0
      ? `All ${filteredCount} ${filteredCount === 1 ? 'favourite' : 'favourites'} shown.`
      : `Showing ${visibleCount} of ${filteredCount} favourites.`);
  }
  function renderFavourites(version) {
    const items = Array.isArray(snapshot.favorites) ? snapshot.favorites : [];
    const toolbar = app.querySelector('[data-for-you-favourites-toolbar]');
    const noMatch = app.querySelector('[data-for-you-favourites-no-match]');
    const loadMore = app.querySelector('[data-collection-load-more]');
    const target = app.querySelector('[data-for-you-favourites]');
    toolbar.hidden = items.length === 0;
    noMatch.hidden = true;
    if (!items.length) {
      resetFavouriteView(); loadMore.hidden = true;
      return renderProducts([], target, version, true);
    }
    syncFavouriteFilters(items);
    const filtered = favouriteViewItems(items);
    favouriteVisibleCount = Math.min(filtered.length, Math.max(favouriteBatchSize(), favouriteVisibleCount));
    const visibleItems = filtered.slice(0, favouriteVisibleCount).map(({ item }) => item);
    const count = app.querySelector('[data-for-you-favourites-count]');
    if (favouriteFilter === FAVORITES_ALL) {
      count.textContent = `${items.length} ${items.length === 1 ? 'favourite' : 'favourites'} saved.`;
    } else {
      const selected = app.querySelector('[data-for-you-favourites-filter]').selectedOptions[0]?.textContent || 'Selected type';
      count.textContent = filtered.length
        ? `${filtered.length} ${selected} ${filtered.length === 1 ? 'favourite' : 'favourites'}. ${items.length} saved overall.`
        : `No ${selected} favourites. ${items.length} saved overall.`;
      app.querySelector('[data-for-you-favourites-no-match-copy]').textContent = `No favourites match ${selected}.`;
    }
    noMatch.hidden = filtered.length !== 0;
    if (!filtered.length) {
      target.replaceChildren(); target.setAttribute('aria-busy', 'false'); loadMore.hidden = true; return Promise.resolve();
    }
    syncFavouriteLoadMore(filtered.length, visibleItems.length);
    return renderProducts(visibleItems, target, version, true);
  }
  function render({ outgoingPanel = panelMotion?.outgoing } = {}) {
    if (!app || !snapshot) return;
    preserveCardFocus();
    const version = ++renderVersion;
    clearCardPlaceholders();
    feedAppendingVersion = null;
    clearDeferredRows();
    app.querySelectorAll('[data-for-you-panel]').forEach(panel => {
      const active = panel.dataset.forYouPanel === view;
      const outgoing = panel === outgoingPanel && !active;
      panel.hidden = !active && !outgoing;
      panel.inert = !active;
    });
    app.querySelectorAll('[data-for-you-view]').forEach(b => { const selected = b.dataset.forYouView === view; b.setAttribute('aria-selected', String(selected)); b.tabIndex = selected ? 0 : -1; });
    if (view === 'feed') {
      app.querySelector('[data-for-you-feed-copy]').textContent = snapshot.profile.quiz || snapshot.profile.favorites.length ? 'Discover products based on your saved preferences and favourites, with room to try something different.' : 'Start exploring. Save your taste preferences or a favourite to make this page more useful.';
      renderProducts(snapshot.feed.items.filter(p => p.available === true).slice(0, visibleCount), app.querySelector('[data-for-you-feed]'), version);
      app.querySelector('[data-for-you-feed-count]').textContent = '';
      renderPersonalizedRows(version);
      deferProducts(snapshot.recentlyViewed || [], app.querySelector('[data-for-you-recent]'), version, true);
    }
    const completion = view === 'favourites' ? renderFavourites(version) : Promise.resolve();
    if (view === 'taste') renderTaste();
    syncHistoryControl();
    return completion;
  }
  function syncHistoryControl() {
    if (!app || !snapshot) return;
    const checkbox = app.querySelector('[data-for-you-history]'); checkbox.checked = snapshot.profile.historyEnabled === true;
    const available = snapshot.capabilities?.browsingHistory === true;
    checkbox.disabled = mutationBusy || (!available && !checkbox.checked);
    const permission = nativeHistoryPermission(window.Shopify?.customerPrivacy);
    const cookieCopy = permission === 'allowed' ? 'Your cookie preferences allow new views to be saved.'
      : permission === 'blocked' ? 'Your cookie preferences currently block new views. Allow preferences and analytics in Cookie settings to use browsing history.'
      : 'Cookie permissions are unavailable, so new views are not saved.';
    app.querySelector('[data-for-you-history-copy]').textContent = !available
      ? `Browsing history isn’t available yet. New views aren’t saved. ${snapshot.history?.count || 0} saved views. You can still clear saved history or turn it off.`
      : `History is ${checkbox.checked ? 'enabled in your account' : 'off'}. ${cookieCopy} ${snapshot.history?.count || 0} saved views, kept for up to ${snapshot.history?.retentionDays || 30} days.`;
  }
  function readBrowserQuiz() { try { const saved = JSON.parse(localStorage.getItem('sigma-coffee-taste-v1')); return saved?.version === 1 && validAnswers(saved.answers) ? saved.answers : null; } catch { return null; } }
  function readTasteForm() {
    const data = new FormData(app.querySelector('[data-for-you-taste]'));
    return Object.fromEntries(questions.map(question => [question.key, data.getAll(question.key)]));
  }
  function clearTasteQuestionError(key) {
    const fieldset = app?.querySelector(`[data-for-you-question="${key}"]`);
    if (!fieldset) return;
    fieldset.querySelectorAll('input').forEach(input => input.removeAttribute('aria-invalid'));
    const error = fieldset.querySelector('[data-for-you-question-error]');
    if (error) { error.hidden = true; error.textContent = ''; }
  }
  function tasteQuestionError(question, answers) {
    const selected = answers[question.key] || [];
    if (!selected.length) return question.multiple ? 'Choose at least one flavour.' : 'Choose one option.';
    if (new Set(selected).size !== selected.length || selected.some(value => !question.options.some(option => option[0] === value))) return 'Choose one of the available options.';
    if (!question.multiple && selected.length !== 1) return 'Choose one option.';
    if (question.multiple && selected.length > 2) return 'Choose up to two flavours.';
    if (selected.includes('any') && selected.length > 1) return 'Choose Surprise me on its own.';
    return '';
  }
  function validateTasteForm(answers) {
    let firstInvalid = null;
    questions.forEach(question => {
      const fieldset = app.querySelector(`[data-for-you-question="${question.key}"]`);
      const message = tasteQuestionError(question, answers);
      const error = fieldset?.querySelector('[data-for-you-question-error]');
      fieldset?.querySelectorAll('input').forEach(input => {
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
      if (error) { error.textContent = message; error.hidden = !message; }
      if (message && !firstInvalid) firstInvalid = fieldset?.querySelector('input');
    });
    if (firstInvalid) {
      firstInvalid.focus({ preventScroll: true });
      firstInvalid.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      return false;
    }
    return validAnswers(answers);
  }
  function renderTaste({ syncValues = false } = {}) {
    const container = app.querySelector('[data-for-you-questions]');
    if (!tasteDraft || tasteDraft.accountKey !== snapshot.accountKey) setTasteDraftFromSnapshot();
    else if (!tasteDraft.dirty && !syncValues) tasteDraft.answers = copyQuiz(snapshot.profile.quiz);
    if (!container.children.length) {
      questions.forEach(question => {
        const card = el('div', '', 'for-you__question');
        const fieldset = el('fieldset');
        const descriptionId = `ForYouTaste-${question.key}-description`;
        const errorId = `ForYouTaste-${question.key}-error`;
        fieldset.dataset.forYouQuestion = question.key;
        fieldset.setAttribute('aria-describedby', `${descriptionId} ${errorId}`);
        fieldset.append(el('legend', question.title));
        const instruction = el('p', `${question.description} ${question.multiple ? 'Choose up to two, or Surprise me on its own.' : 'Choose one.'}`, 'for-you__copy for-you__question-instruction');
        instruction.id = descriptionId;
        fieldset.append(instruction);
        const choices = el('div', '', 'for-you__question-options');
        question.options.forEach(([value, title]) => {
          const label = el('label', '', 'for-you__choice');
          const input = el('input');
          input.type = question.multiple ? 'checkbox' : 'radio';
          input.name = question.key;
          input.value = value;
          input.setAttribute('aria-describedby', `${descriptionId} ${errorId}`);
          label.append(input);
          if (question.multiple) label.append(createCheckboxIndicator());
          label.append(el('span', title));
          choices.append(label);
        });
        const error = el('p', '', 'for-you__question-error');
        error.id = errorId;
        error.dataset.forYouQuestionError = '';
        error.hidden = true;
        fieldset.append(choices, error);
        card.append(fieldset);
        container.append(card);
      });
    }
    const answers = tasteDraft?.answers || emptyQuiz();
    questions.forEach(question => {
      app.querySelectorAll(`[data-for-you-question="${question.key}"] input`).forEach(input => { input.checked = answers[question.key]?.includes(input.value) || false; });
    });
    setTasteBusy(mutationBusy, tasteMutationIntent);
    app.querySelector('[data-for-you-import]').hidden = !readBrowserQuiz();
  }
  if (app) {
    const nativeFavouriteControls = app.querySelector('[data-for-you-favourites-native-controls]');
    const customFavouriteControls = app.querySelector('[data-for-you-favourites-dialog-controls]');
    const canEnhanceFavouriteControls = typeof HTMLDialogElement === 'function'
      && typeof HTMLDialogElement.prototype.showModal === 'function';
    if (canEnhanceFavouriteControls) {
      const productTypeSheet = initializeFavouriteSelectDialog('filter');
      const sortSheet = initializeFavouriteSelectDialog('sort');
      if (productTypeSheet && sortSheet) {
        nativeFavouriteControls.hidden = true;
        customFavouriteControls.hidden = false;
        syncFavouriteSelectDialogs();
      }
    }
    app.querySelectorAll('[data-for-you-decorate]').forEach(node => { const icon = icons.get(node.dataset.forYouDecorate); if (icon) { node.prepend(icon.content.cloneNode(true)); node.dataset.iconText = ''; } });
    app.querySelectorAll('[data-for-you-view]').forEach(b => {
      b.addEventListener('click', event => navigate(b.dataset.forYouView, event));
      b.addEventListener('focus', () => b.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
      b.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); const tabs = [...app.querySelectorAll('[data-for-you-view]:not(:disabled):not([aria-disabled="true"])')];
        const index = tabs.indexOf(b); const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs.forEach((tab, i) => { tab.tabIndex = i === next ? 0 : -1; });
        tabs[next].focus();
      });
    });
    app.querySelector('[data-for-you-feed]').addEventListener('scroll', appendFeed, { passive: true });
    app.querySelector('[data-for-you-favourites-filter]').addEventListener('change', event => {
      favouriteFilter = event.currentTarget.value; favouriteVisibleCount = favouriteBatchSize(); render();
    });
    app.querySelector('[data-for-you-favourites-sort]').addEventListener('change', event => {
      favouriteSort = event.currentTarget.value; favouriteVisibleCount = favouriteBatchSize(); render();
    });
    app.querySelector('[data-for-you-favourites-clear]').addEventListener('click', () => {
      favouriteFilter = FAVORITES_ALL; favouriteVisibleCount = favouriteBatchSize(); render();
      requestAnimationFrame(() => app.querySelector('[data-for-you-favourites-filter]')?.focus({ preventScroll: true }));
    });
    const favouriteLoadMore = app.querySelector('[data-collection-load-more]');
    const favouriteLoadMoreButton = favouriteLoadMore.querySelector('[data-collection-load-more-button]');
    favouriteLoadMoreButton.addEventListener('click', async event => {
      if (favouriteLoadMoreBusy || !snapshot || view !== 'favourites') return;
      const filteredCount = favouriteViewItems(Array.isArray(snapshot.favorites) ? snapshot.favorites : []).length;
      const visibleBefore = Math.min(favouriteVisibleCount, filteredCount);
      if (visibleBefore >= filteredCount) return;
      const keyboardActivation = event.detail === 0;
      const accountKey = snapshot.accountKey;
      const scrollPosition = { left: window.scrollX, top: window.scrollY };
      favouriteLoadMoreBusy = true;
      favouriteLoadMoreButton.disabled = true;
      setFavouriteLoading(favouriteLoadMoreButton, true);
      favouriteVisibleCount = Math.min(filteredCount, visibleBefore + favouriteBatchSize());
      const completion = render();
      const version = renderVersion;
      try {
        await completion;
        if (snapshot?.accountKey !== accountKey || view !== 'favourites' || version !== renderVersion) return;
        window.scrollTo(scrollPosition);
        const visibleAfter = Math.min(favouriteVisibleCount, filteredCount);
        const loaded = visibleAfter - visibleBefore;
        const remaining = filteredCount - visibleAfter;
        syncFavouriteLoadMore(filteredCount, visibleAfter, remaining === 0
          ? `${loaded} more loaded. All ${filteredCount} ${filteredCount === 1 ? 'favourite' : 'favourites'} shown.`
          : `${loaded} more loaded. Showing ${visibleAfter} of ${filteredCount} favourites.`);
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (keyboardActivation) {
          const cards = [...app.querySelector('[data-for-you-favourites]').children];
          const firstLink = cards.slice(visibleBefore).map(card => card.querySelector('[data-product-card-link], a[href]')).find(Boolean);
          const focusTarget = firstLink || app.querySelector('[data-for-you-favourites-count]');
          if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
        }
      } finally {
        if (snapshot?.accountKey === accountKey) {
          favouriteLoadMoreBusy = false;
          favouriteLoadMoreButton.disabled = false;
          setFavouriteLoading(favouriteLoadMoreButton, false);
        }
      }
    });
    let favouriteResizeFrame = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(favouriteResizeFrame);
      favouriteResizeFrame = requestAnimationFrame(() => {
        if (!snapshot || view !== 'favourites' || favouriteLoadMoreBusy) return;
        const minimum = favouriteBatchSize();
        if (minimum > favouriteVisibleCount) {
          favouriteVisibleCount = minimum;
          render();
          return;
        }
        const filteredCount = favouriteViewItems(Array.isArray(snapshot.favorites) ? snapshot.favorites : []).length;
        syncFavouriteLoadMore(filteredCount, Math.min(favouriteVisibleCount, filteredCount));
      });
    }, { passive: true });
    app.querySelector('[data-for-you-undo]').addEventListener('click', async () => { const previous = undo; if (!previous || previous.accountKey !== snapshot?.accountKey) return; undo = null; await change('less_like', { productId: previous.id, enabled: false }, 'Recommendation feedback undone.'); app.querySelector('[data-for-you-undo]').hidden = true; });
    app.querySelector('[data-for-you-taste]').addEventListener('change', event => {
      if (!event.target.matches('[data-for-you-question] input')) return;
      tasteDraft = { accountKey: snapshot.accountKey, answers: readTasteForm(), dirty: true };
      clearTasteQuestionError(event.target.name);
      tasteTell('');
    });
    app.querySelector('[data-for-you-taste]').addEventListener('submit', event => {
      event.preventDefault();
      const quiz = readTasteForm();
      tasteDraft = { accountKey: snapshot.accountKey, answers: quiz, dirty: true };
      if (!validateTasteForm(quiz)) { tasteTell('Check the highlighted questions.'); return; }
      change('save_quiz', { quiz }, 'Your taste preferences are saved.', { taste: true, tasteIntent: 'save' });
    });
    app.querySelector('[data-for-you-clear-taste]').addEventListener('click', () => change('save_quiz', { quiz: null }, 'Taste preferences cleared.', { taste: true, tasteIntent: 'clear' }));
    app.querySelector('[data-for-you-import]').addEventListener('click', () => {
      const quiz = readBrowserQuiz();
      if (quiz) change('save_quiz', { quiz }, 'This browser’s taste profile is now saved to your account.', { taste: true, tasteIntent: 'import' });
    });
    app.querySelector('[data-for-you-history]').addEventListener('change', event => change('history_consent', { enabled: event.target.checked }, event.target.checked ? 'History enabled. Cookie preferences still apply.' : 'History is off. Saved views have been cleared.'));
    app.querySelector('[data-for-you-clear-history]').addEventListener('click', () => change('clear_history', {}, 'Browsing history cleared.'));
    const confirm = app.querySelector('[data-for-you-reset-confirm]');
    app.querySelector('[data-for-you-reset]').addEventListener('click', () => { confirm.hidden = false; confirm.querySelector('button').focus(); });
    app.querySelector('[data-for-you-reset-no]').addEventListener('click', () => { confirm.hidden = true; app.querySelector('[data-for-you-reset]').focus(); });
    app.querySelector('[data-for-you-reset-yes]').addEventListener('click', async () => { if (await change('reset', {}, 'For You data cleared. Browsing history is off.')) { confirm.hidden = true; undo = null; app.querySelector('[data-for-you-undo]').hidden = true; } });
  }
  function maybeRecordView() {
    const id = productId(config.dataset.currentProduct);
    if (fixture || hasViewed || !id || mutationBusy || document.hidden || !allowHistory(snapshot, window.Shopify?.customerPrivacy)) return;
    hasViewed = true;
    change('view', { productId: id, personalizationConsent: true }, '').then(() => {});
  }
  const privacyReady = () => { syncHistoryControl(); maybeRecordView(); };
  document.addEventListener('visitorConsentCollected', privacyReady);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', privacyReady);
  if (!fixture) loadHistoryPrivacy(window.Shopify, privacyReady);
  window.addEventListener('pagehide', () => { pageLeaving = true; window.clearTimeout(loading?.forYouLoadingTimer); finishPanelMotion(); clearCardPlaceholders(); saveQueue.suspend(); if (!fixture) client.invalidate({ keepMutation: true }); undo = null; });
  window.addEventListener('pageshow', event => { if (event.persisted) { pageLeaving = false; load(); } });
  document.addEventListener('visibilitychange', () => { if (!fixture && !document.hidden && !mutationBusy) load(); });
  retry?.addEventListener('click', load);
  // Explicit profile transfer only. A fresh server response establishes the account.
  window.addEventListener('sigma:for-you-readiness', () => document.querySelectorAll('[data-for-you-quiz-save]').forEach(node => { node.hidden = !window.SigmaForYou?.readyForSave; }));
  window.SigmaForYou = {
    get readyForSave() { return serviceStatus === 'ready' || serviceStatus === 'guest'; },
    async saveQuiz(quiz) {
      if (!validAnswers(quiz)) return { ok: false, message: 'Complete your taste profile first.' };
      if (!snapshot && !fixture) {
        if (serviceStatus !== 'guest') return { ok: false, message: 'Account saving isn’t available right now. You can still save this profile in your browser.' };
        try { sessionStorage.setItem('sigma-for-you-pending-quiz', JSON.stringify({ quiz, expires: Date.now() + 600000 })); } catch { return { ok: false, message: 'Save your profile in this browser, then log in and import it from Your taste.' }; }
        window.location.assign(config.dataset.loginUrl); return { ok: false, message: 'Log in, then confirm that you want to save this profile to your account.' };
      }
      const ok = await change('save_quiz', { quiz }, 'Taste profile saved to your account.');
      return { ok, message: ok ? 'Saved to your account for For You.' : 'Unable to confirm the save. Check For You before trying again.' };
    },
  };
  if (!fixture && config.dataset.authenticated === 'false') {
    settleGuest({ preserveServerFallback: true });
  } else {
    await load();
  }
  if (snapshot && !fixture) {
    try {
      const pending = JSON.parse(sessionStorage.getItem('sigma-for-you-pending-favourite'));
      sessionStorage.removeItem('sigma-for-you-pending-favourite');
      if (pending?.expires > Date.now() && productId(pending.productId)) {
        const prompt = el('aside', '', 'for-you__notice for-you__pending'); prompt.dataset.forYouPendingConfirmation = '';
        prompt.append(el('p', `Save ${String(pending.title).slice(0, 200)} to this account’s favourites?`));
        const key = snapshot.accountKey;
        prompt.append(button('Save favourite', async () => {
          if (snapshot?.accountKey !== key) { prompt.remove(); return; }
          if (await change('favorite', { productId: pending.productId, enabled: true }, 'Saved to your favourites.')) prompt.remove();
        }), button('Keep browsing', () => prompt.remove()));
        (document.querySelector('main') || document.body).prepend(prompt);
      }
    } catch { /* Never infer or transfer an intent from another storage source. */ }
  }
  const finder = document.querySelector('[data-coffee-finder]');
  if (finder && snapshot && !fixture) {
    try {
      const pending = JSON.parse(sessionStorage.getItem('sigma-for-you-pending-quiz'));
      sessionStorage.removeItem('sigma-for-you-pending-quiz');
      if (pending?.expires > Date.now() && validAnswers(pending.quiz)) {
        const prompt = el('aside', '', 'for-you__notice'); prompt.dataset.forYouPendingConfirmation = ''; prompt.append(el('p', 'Save the taste profile you chose before signing in to this account?'));
        const key = snapshot.accountKey;
        prompt.append(button('Save to my account', async () => { if (snapshot?.accountKey !== key) { prompt.remove(); return; } if (await change('save_quiz', { quiz: pending.quiz }, 'Taste profile saved to your account.')) prompt.remove(); }), button('Keep browsing', () => prompt.remove()));
        finder.prepend(prompt);
      }
    } catch { /* No implicit transfer or save when storage is unavailable. */ }
  }
}
